import { LanguageCode } from '../languages';

export type LocalizationProps = { degree: string };

export enum StoreType {
  US = 'us',
  NON_US = 'nonUS',
}

export type StorePagesProps = {
  url: string;
  storeName: string;
  language?: string;
  plan?: string;
  type?: StoreType;
};

export type ProductProps = {
  key: string;
  nameWithCode?: string;
  name: string;
  videoToolID: number;
  degreeName?: string;
  storePages?: StorePagesProps[];
};

type StorePageArgs = {
  storeName: string;
  language?: LanguageCode;
  type?: StoreType;
  profession?: string;
};

export class StorePage {
  storeName: string;
  language: LanguageCode;
  type: StoreType;
  profession?: string;
  translatedLibrary?: boolean;

  constructor(
    {
      storeName,
      language = LanguageCode.EN,
      type = StoreType.US,
      profession,
    }: StorePageArgs,
    key: string,
  ) {
    this.storeName = storeName;
    this.type = type;
    this.language = language;
    this.profession = profession || storeName;
    this.translatedLibrary = translatedLibraries.includes(key);
  }
}

export const translatedLibraries = ['MD', 'MD_nonUSMLE', 'OH', 'NP'];

/**
 * @class Product
 */
export class Product {
  name: string;
  key: string;
  nameWithCode: string;
  url: string;
  storePages: StorePage[];
  constructor({
    name,
    key,
    nameWithCode,
    url,
    storePages = [],
  }: {
    url: string;
    name: string;
    key: string;
    nameWithCode: string;
    storePages: StorePageArgs[];
  }) {
    this.url = url;
    this.name = name;
    this.key = key;
    this.nameWithCode = nameWithCode;
    this.storePages = storePages.map((spArgs) => new StorePage(spArgs, key));
  }
}
const pharmacyProduct = new Product({
  key: 'PH',
  name: 'Pharmacy',
  nameWithCode: 'Pharmacy (PH)',
  url: 'ph',
  storePages: [
    {
      storeName: 'Pharmacy',
      profession: 'Pharmacist',
    },
    {
      storeName: 'Pharmacy',
      type: StoreType.NON_US,
      profession: 'Pharmacist',
    },
    {
      type: StoreType.NON_US,
      language: LanguageCode.ES,
      storeName: 'Farmacia',
      profession: 'Farmacéutico',
    },
  ],
});
const lpnProduct = new Product({
  key: 'LPN',
  name: 'Licensed Practical Nurse',
  nameWithCode: 'Licensed Practical Nurse (LPN)',
  url: 'lpn',
  storePages: [
    {
      storeName: 'Licensed Practical Nursing (LPN)',
      profession: 'Licensed Practical Nurse',
    },
    {
      type: StoreType.NON_US,
      storeName: 'Licensed Practical Nursing',
      profession: 'Licensed Practical Nurse',
    },
    {
      type: StoreType.NON_US,
      language: LanguageCode.ES,
      storeName: 'Enfermería práctica con licencia (LPN)',
      profession: 'Enfermera practicante licenciada',
    },
  ],
});

const dentistryProduct = new Product({
  key: 'DN',
  name: 'Dentistry',
  nameWithCode: 'Dentistry (DN)',
  url: 'dn',
  storePages: [
    {
      storeName: 'Dentistry',
    },
    {
      type: StoreType.NON_US,
      storeName: 'Dentistry',
    },
    {
      type: StoreType.NON_US,
      language: LanguageCode.ES,
      storeName: 'Odontología',
      profession: 'Dentista',
    },
  ],
});
export const list = [
  new Product({
    key: 'MD',
    name: 'Medicine',
    nameWithCode: 'Medicine (MD)',
    url: 'md',
    storePages: [
      {
        storeName: 'Medicine (MD)',
        profession: 'Medical Doctor',
      },
      {
        type: StoreType.NON_US,
        storeName: 'Medicine (USMLE)',
        profession: 'Medical Doctor',
      },
      {
        language: LanguageCode.ES,
        type: StoreType.NON_US,
        storeName: 'Medicina (con USMLE)',
        profession: 'Doctor medico (con USMLE)',
      },
    ],
  }),
  // In the future, we will provide a non-USMLE option during onboarding, but this will not be part of the initial React Onboarding release.
  new Product({
    key: 'MD_nonUSMLE',
    name: 'Health Professional',
    nameWithCode: 'Health Professional (OH)',
    url: 'oh',
    storePages: [
      {
        type: StoreType.NON_US,
        storeName: 'Medicine (non-USMLE)',
        profession: 'Medical Doctor (non-USMLE)',
      },
      {
        language: LanguageCode.ES,
        type: StoreType.NON_US,
        storeName: 'Medicina (sin USMLE)',
        profession: 'Doctor medico (sin USMLE)',
      },
    ],
  }),
  new Product({
    key: 'DO',
    name: 'Medicine (DO)',
    nameWithCode: 'Medicine (DO)',
    url: 'do',
    storePages: [
      {
        type: StoreType.US,
        storeName: 'Medicine (DO)',
        profession: 'Osteopathic Doctor',
      },
      {
        type: StoreType.NON_US,
        storeName: 'Medicine (Osteopathy)',
        profession: 'Osteopathic Doctor',
      },
      {
        language: LanguageCode.ES,
        type: StoreType.NON_US,
        storeName: 'Medicina (Osteopatía)',
        profession: 'Médico Osteópata',
      },
    ],
  }),
  new Product({
    key: 'PA',
    name: 'Physician Assistant',
    nameWithCode: 'Physician Assistant (PA)',
    url: 'pa',
    storePages: [
      {
        storeName: 'Physician Assistant (PA)',
        profession: 'Physician Assistant',
      },
      {
        type: StoreType.NON_US,
        storeName: 'Physician Assistant',
      },
      {
        language: LanguageCode.ES,
        type: StoreType.NON_US,
        storeName: 'Asociado médico',
      },
    ],
  }),
  new Product({
    key: 'NP',
    name: 'Nurse Practitioner',
    nameWithCode: 'Nurse Practitioner (NP)',
    url: 'np',
    storePages: [
      {
        storeName: 'Nurse Practitioner (NP)',
        profession: 'Nurse Practitioner',
      },
      {
        type: StoreType.NON_US,
        storeName: 'Nurse Practitioner',
      },
      {
        type: StoreType.NON_US,
        language: LanguageCode.ES,
        storeName: 'Enfermería - practicante (NP)',
      },
    ],
  }),
  new Product({
    key: 'RN',
    name: 'Nursing',
    nameWithCode: 'Nursing (RN)',
    url: 'rn',
    storePages: [
      {
        storeName: 'Registered Nursing (RN)',
        profession: 'Registered Nurse',
      },
      {
        type: StoreType.NON_US,
        storeName: 'Registered Nursing',
        profession: 'Registered Nurse',
      },
      {
        type: StoreType.NON_US,
        language: LanguageCode.ES,
        storeName: 'Enfermería - registrada (RN)',
        profession: 'Enfermero Registrado',
      },
    ],
  }),
  lpnProduct,
  dentistryProduct,
  pharmacyProduct,
  new Product({
    key: 'OH',
    name: 'Health Professional',
    nameWithCode: 'Health Professional (OH)',
    url: 'oh',
    storePages: [
      {
        storeName: 'Health Professional',
      },
      {
        storeName: 'Health Professional',
        type: StoreType.NON_US,
      },
      {
        language: LanguageCode.ES,
        type: StoreType.NON_US,
        storeName: 'Profesional de salud',
      },
    ],
  }),
];
export const onboardingDropdownProducts = [
  dentistryProduct,
  lpnProduct,
  pharmacyProduct,
  new Product({
    key: 'OH',
    name: 'Physical Therapy',
    nameWithCode: 'Physical Therapy (OH)',
    url: 'oh',
    storePages: [
      {
        storeName: 'Physical Therapy',
        profession: 'Physical Therapist',
      },
      {
        storeName: 'Physical Therapy',
        type: StoreType.NON_US,
        profession: 'Physical Therapist',
      },
      {
        type: StoreType.NON_US,
        language: LanguageCode.ES,
        storeName: 'Terapia física',
        profession: 'Fisioterapeuta',
      },
    ],
  }),
  new Product({
    key: 'OH',
    name: 'Podiatry',
    nameWithCode: 'Podiatry (OH)',
    url: 'oh',
    storePages: [
      {
        storeName: 'Podiatry',
        profession: 'Podiatrist',
      },
      {
        storeName: 'Podiatry',
        type: StoreType.NON_US,
        profession: 'Podiatrist',
      },
      {
        type: StoreType.NON_US,
        language: LanguageCode.ES,
        storeName: 'Podología',
        profession: 'Podólogo',
      },
    ],
  }),
  new Product({
    key: 'OH',
    name: 'Veterinary Medicine',
    nameWithCode: 'Veterinary Medicine (OH)',
    url: 'oh',
    storePages: [
      {
        storeName: 'Veterinary Medicine',
        profession: 'Veterinarian',
      },
      {
        storeName: 'Veterinary Medicine',
        type: StoreType.NON_US,
        profession: 'Veterinarian',
      },
      {
        type: StoreType.NON_US,
        language: LanguageCode.ES,
        storeName: 'Podología',
        profession: 'Medicina Veterinaria',
      },
    ],
  }),
  new Product({
    key: 'OH',
    name: 'Faculty',
    nameWithCode: 'Faculty (OH)',
    url: 'oh',
    storePages: [
      {
        storeName: 'Faculty',
        profession: 'Faculty',
      },
      {
        storeName: 'Faculty',
        type: StoreType.NON_US,
        profession: 'Faculty',
      },
      {
        type: StoreType.NON_US,
        language: LanguageCode.ES,
        storeName: 'Facultad',
        profession: 'Facultad',
      },
    ],
  }),
  new Product({
    key: 'OH',
    name: 'Health Professional',
    nameWithCode: 'Health Professional (OH)',
    url: 'oh',
    storePages: [
      {
        storeName: 'Health Professional',
      },
      {
        storeName: 'Health Professional',
        type: StoreType.NON_US,
      },
      {
        language: LanguageCode.ES,
        type: StoreType.NON_US,
        storeName: 'Profesional de salud',
      },
    ],
  }),
];

export const keys: string[] = [];
export const byKeys: Record<string, Product> = {};

for (const product of list) {
  keys.push(product.key);

  // eslint-disable-next-line no-prototype-builtins
  if (byKeys.hasOwnProperty(product.key)) {
    // eslint-disable-next-line no-console
    console.warn(`DUPLICATE PRODUCT: ${product.key}`);
  }

  byKeys[product.key] = product;
}
